/**
 * 设置页
 * ------------------------------------------------------------
 * - 学习偏好（语速、自动发音、音标显示、每日目标）
 * - AI 补全配置（默认关闭，需自己填 Key）
 * - 数据管理：导出 / 导入备份、清空学习记录
 * - 存储信息：当前适配器类型（IndexedDB / 内存）+ 未来云端说明
 */
import { state, saveSettings, updateStudent, listStudents, switchStudent, createStudent } from '../core/store.js';
import { getStorage } from '../core/storage/index.js';
import { COLLECTIONS, LEARNING_COLLECTIONS, CONTENT_COLLECTIONS } from '../core/schema.js';
import { loadLLMConfig, saveLLMConfig } from '../ai/llmAdapter.js';
import { card, confirmDialog, emptyState, tag } from '../components/ui.js';
import { downloadJSON, escapeHtml, toast, fmtDate, speakEn, speakZh, voiceInfo, preloadVoices } from '../utils/index.js';
import { navigate } from '../core/router.js';
import { emit, EVENTS } from '../core/events.js';

export default async function renderSettings(mount) {
  const [llm, students] = await Promise.all([loadLLMConfig(), listStudents()]);
  const storage = getStorage();
  const s = state.settings;

  mount.innerHTML = `
    <div class="page-head">
      <h1>⚙️ 设置</h1>
      <p class="lead">学习偏好、AI 补全与数据管理。所有设置保存在本机浏览器。</p>
    </div>

    <div class="split">
      <div class="col" style="gap:16px">

        ${card(`
          <div class="form-grid">
            <label class="switch-row">
              <span>自动发音</span>
              <input type="checkbox" id="set-autoSpeak" ${s.autoSpeak ? 'checked' : ''}>
            </label>
            <label class="switch-row">
              <span>显示音标</span>
              <input type="checkbox" id="set-showPhonetic" ${s.showPhonetic ? 'checked' : ''}>
            </label>
            <label class="switch-row">
              <span>答题音效</span>
              <input type="checkbox" id="set-soundEffect" ${s.soundEffect ? 'checked' : ''}>
            </label>
            <label class="switch-row">
              <span>允许跳过题目</span>
              <input type="checkbox" id="set-allowSkip" ${s.allowSkip ? 'checked' : ''}>
            </label>
            <label>语音速度 <b id="rate-val">${s.speakRate}</b>x
              <input type="range" id="set-speakRate" min="0.5" max="1.4" step="0.1" value="${s.speakRate}">
            </label>
            <label>每日目标题量
              <input class="input" type="number" id="set-dailyGoal" min="5" max="200" step="5" value="${s.dailyGoal}">
            </label>
          </div>
          <div class="row mt-8">
            <button class="btn btn-ghost btn-sm" id="test-speak" type="button">🔊 试听英文</button>
            <button class="btn btn-ghost btn-sm" id="test-speak-zh" type="button">🗣️ 试听中文</button>
          </div>
          <p class="muted small" id="voice-info">正在检测语音…</p>`,
          { title: '学习偏好' })}

        ${card(`
          <div class="row between" style="align-items:flex-start">
            <p class="muted" style="font-size:12.5px;margin:0;max-width:520px">
              AI 补全用于识别 PDF 时补全缺失的<b>音标</b>或<b>中文意思</b>。默认关闭。
              开启后模型被强制要求"原文没有就返回 null"，输出仍标记为「待确认」，必须人工核对。
              支持任何 OpenAI 兼容接口（OpenAI / DeepSeek / 通义 / Ollama 等）。
            </p>
            <label class="switch-row" style="min-width:120px">
              <span>启用</span>
              <input type="checkbox" id="llm-enabled" ${llm.enabled ? 'checked' : ''}>
            </label>
          </div>
          <div class="form-grid mt-8">
            <label>接口地址 baseUrl
              <input class="input mono" id="llm-baseUrl" value="${escapeHtml(llm.baseUrl)}" placeholder="https://api.openai.com/v1">
            </label>
            <label>模型名
              <input class="input mono" id="llm-model" value="${escapeHtml(llm.model)}" placeholder="gpt-4o-mini">
            </label>
            <label>API Key
              <input class="input mono" id="llm-apiKey" type="password" value="${escapeHtml(llm.apiKey)}" placeholder="sk-...">
            </label>
          </div>
          <div class="row mt-8" style="gap:8px">
            <button class="btn btn-primary btn-sm" id="save-llm" type="button">保存 AI 配置</button>
            <span class="muted" style="font-size:11.5px;align-self:center">
              Key 只保存在本机浏览器，不会上传到任何第三方服务（只有调用你自己配置的接口时才会用到）
            </span>
          </div>`,
          { title: 'AI 补全（可选）', extra: tag(llm.enabled && llm.apiKey ? '已启用' : '未启用', llm.enabled && llm.apiKey ? 'green' : '') })}

        ${card(`
          <div class="draft-list">
            ${students.map(st => `
              <div class="draft-row">
                <span class="student-avatar">${st.avatar || '🐻'}</span>
                <div class="grow">
                  <div style="font-size:13.5px;font-weight:600">${escapeHtml(st.name)}</div>
                  <div class="muted" style="font-size:11.5px">${st.xp || 0} XP · 创建于 ${fmtDate(st.createdAt)}</div>
                </div>
                ${st.id === state.student?.id
                  ? '<span class="tag green">当前</span>'
                  : `<button class="btn btn-sm btn-ghost" data-switch="${st.id}" type="button">切换</button>`}
              </div>`).join('')}
          </div>
          <div class="row mt-8" style="gap:8px">
            <input class="input" id="new-name" placeholder="新学习者名字" maxlength="12" style="max-width:180px">
            <button class="btn btn-ghost btn-sm" id="add-student" type="button">添加学习者</button>
          </div>`,
          { title: '学习者' })}

      </div>

      <div class="col" style="gap:16px">
        ${card(`
          <div class="info-list">
            <div><span class="muted">存储适配器</span><b>${storage.kind}</b></div>
            <div><span class="muted">数据位置</span><b>本机浏览器</b></div>
            <div><span class="muted">应用版本</span><b>v1.1.0</b></div>
          </div>
          <p class="muted mt-8" style="font-size:12px">
            ${storage.kind === 'indexeddb'
              ? '数据保存在浏览器 IndexedDB。清理浏览器数据会丢失，建议定期导出备份。'
              : '当前使用内存存储（刷新即清空），仅用于演示/测试。'}
          </p>`,
          { title: '存储信息' })}

        ${card(`
          <div class="col" style="gap:8px">
            <button class="btn btn-ghost btn-block" id="export-all" type="button">⬇️ 导出全部数据（JSON）</button>
            <button class="btn btn-ghost btn-block" id="export-learning" type="button">⬇️ 只导出学习记录</button>
            <button class="btn btn-ghost btn-block" id="import-backup" type="button">⬆️ 导入备份文件</button>
            <input type="file" id="backup-file" accept=".json" hidden>
            <hr style="border:none;border-top:1px solid var(--border);margin:6px 0">
            <button class="btn btn-danger btn-block" id="clear-progress" type="button">🧹 清空学习记录</button>
            <button class="btn btn-danger btn-block" id="clear-all" type="button">⚠️ 清空全部数据</button>
          </div>`,
          { title: '数据管理' })}

        ${card(`
          <p class="muted" style="font-size:12.5px">
            数据层通过 <code class="mono">StorageAdapter</code> 接口与界面解耦。
            未来要做多设备同步，只需新增一个云端适配器（如 Supabase/REST），
            在 <code class="mono">src/core/storage/index.js</code> 里换成默认实现即可，页面代码不用改。
          </p>
          <p class="muted" style="font-size:12.5px">
            详见项目根目录 <code class="mono">PROJECT.md</code> 的「存储层扩展」章节。
          </p>`,
          { title: '关于跨设备同步' })}
      </div>
    </div>`;

  bindSettings(mount, llm);
  bindData(mount);
}

/* ------------------------------ 偏好 ------------------------------ */

function bindSettings(mount, llm) {
  const save = async patch => {
    await saveSettings(patch);
    toast('已保存');
  };

  const bindCheck = (id, key) => {
    mount.querySelector(id)?.addEventListener('change', e => save({ [key]: e.target.checked }));
  };
  bindCheck('#set-autoSpeak', 'autoSpeak');
  bindCheck('#set-showPhonetic', 'showPhonetic');
  bindCheck('#set-soundEffect', 'soundEffect');
  bindCheck('#set-allowSkip', 'allowSkip');

  const rate = mount.querySelector('#set-speakRate');
  rate?.addEventListener('input', () => {
    mount.querySelector('#rate-val').textContent = rate.value;
  });
  rate?.addEventListener('change', () => save({ speakRate: Number(rate.value) }));

  mount.querySelector('#set-dailyGoal')?.addEventListener('change', e => {
    const v = Math.max(5, Math.min(200, Number(e.target.value) || 30));
    e.target.value = v;
    save({ dailyGoal: v });
  });

  preloadVoices();
  const vi = voiceInfo();
  const voiceBox = mount.querySelector('#voice-info');
  if (voiceBox) {
    voiceBox.textContent = vi.supported
      ? `英语语音：${vi.english}　｜　中文语音：${vi.chinese}`
      : '当前浏览器不支持语音合成，发音按钮将不可用';
  }
  mount.querySelector('#test-speak')?.addEventListener('click', () => {
    speakEn('Hello! Let us learn English together.', Number(rate?.value || 0.9));
  });
  mount.querySelector('#test-speak-zh')?.addEventListener('click', () => {
    // 中文测试用中文语音，绝不混用英语引擎
    speakZh('你好，我们一起来学英语吧。');
  });

  mount.querySelector('#save-llm')?.addEventListener('click', async () => {
    await saveLLMConfig({
      enabled: mount.querySelector('#llm-enabled').checked,
      baseUrl: mount.querySelector('#llm-baseUrl').value.trim() || 'https://api.openai.com/v1',
      model: mount.querySelector('#llm-model').value.trim() || 'gpt-4o-mini',
      apiKey: mount.querySelector('#llm-apiKey').value.trim(),
    });
    toast('AI 配置已保存', 'success');
    emit(EVENTS.SETTINGS_CHANGED);
  });

  mount.querySelectorAll('[data-switch]').forEach(b => {
    b.addEventListener('click', async () => {
      await switchStudent(b.dataset.switch);
      toast('已切换学习者');
      navigate('#/settings');
    });
  });
  mount.querySelector('#add-student')?.addEventListener('click', async () => {
    const name = mount.querySelector('#new-name').value.trim();
    if (!name) return toast('请填写名字', 'warn');
    await createStudent({ name });
    toast('已添加学习者');
    navigate('#/settings');
  });
}

/* ------------------------------ 数据 ------------------------------ */

function bindData(mount) {
  const storage = getStorage();

  const dumpCollections = async (names) => {
    const out = { exportedAt: new Date().toISOString(), version: 1, data: {} };
    for (const name of names) {
      out.data[name] = await storage.list(name);
    }
    return out;
  };

  mount.querySelector('#export-all')?.addEventListener('click', async () => {
    const dump = await dumpCollections([...CONTENT_COLLECTIONS, ...LEARNING_COLLECTIONS]);
    downloadJSON(`english-app-backup-${new Date().toISOString().slice(0, 10)}.json`, dump);
    toast('已导出全部数据', 'success');
  });

  mount.querySelector('#export-learning')?.addEventListener('click', async () => {
    const dump = await dumpCollections(LEARNING_COLLECTIONS);
    downloadJSON(`english-app-learning-${new Date().toISOString().slice(0, 10)}.json`, dump);
    toast('已导出学习记录', 'success');
  });

  const fileInput = mount.querySelector('#backup-file');
  mount.querySelector('#import-backup')?.addEventListener('click', () => fileInput.click());
  fileInput?.addEventListener('change', async () => {
    const file = fileInput.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const dump = JSON.parse(text);
      if (!dump.data) throw new Error('不是本系统的备份文件');
      const ok = await confirmDialog({
        title: '导入备份',
        message: '备份内容会合并到当前数据（同 ID 会被覆盖）。确定继续吗？',
        okText: '导入',
      });
      if (!ok) return;
      let n = 0;
      for (const [name, rows] of Object.entries(dump.data)) {
        if (!Array.isArray(rows) || !rows.length) continue;
        await storage.bulkPut(name, rows);
        n += rows.length;
      }
      toast(`已恢复 ${n} 条记录`, 'success');
      emit(EVENTS.CONTENT_CHANGED);
      emit(EVENTS.PROGRESS_CHANGED);
      navigate('#/settings');
    } catch (err) {
      toast('导入失败：' + err.message, 'error');
    }
  });

  mount.querySelector('#clear-progress')?.addEventListener('click', async () => {
    const ok = await confirmDialog({
      title: '清空学习记录',
      message: '会删除所有练习记录、答题明细、错题与掌握情况，教材内容不受影响。此操作不可撤销。',
      okText: '确定清空',
      danger: true,
    });
    if (!ok) return;
    for (const name of [COLLECTIONS.SESSIONS, COLLECTIONS.ANSWERS, COLLECTIONS.ERRORS, COLLECTIONS.MASTERY, COLLECTIONS.ACHIEVEMENTS]) {
      await storage.clear(name);
    }
    await updateStudent({ xp: 0 });
    toast('学习记录已清空', 'success');
    emit(EVENTS.PROGRESS_CHANGED);
    navigate('#/settings');
  });

  mount.querySelector('#clear-all')?.addEventListener('click', async () => {
    const ok = await confirmDialog({
      title: '清空全部数据',
      message: '包括导入的教材内容与全部学习记录。内置的 data/textbooks 文件不受影响，刷新后会重新读取。此操作不可撤销。',
      okText: '全部清空',
      danger: true,
    });
    if (!ok) return;
    for (const name of Object.keys(COLLECTIONS).map(k => COLLECTIONS[k])) {
      await storage.clear(name);
    }
    toast('已清空，正在重新加载…', 'success');
    setTimeout(() => location.reload(), 600);
  });
}
